import type { ProcessRunDetailDto, ProcessRunNodeStateDto } from '@api/processRuns';
import type { ExecutionStep } from './dock/ExecutionLog';
import { topoSortNodeIds, type TopoEdge, type TopoNode } from './topoSort';

/**
 * Latest attempt per node — a node inside a loop, or one that was retried,
 * has one row per iteration and only the last one describes where it stands.
 */
export function latestStateByNode(
  states: readonly ProcessRunNodeStateDto[],
): Map<string, ProcessRunNodeStateDto> {
  const latest = new Map<string, ProcessRunNodeStateDto>();
  for (const ns of states) {
    const cur = latest.get(ns.nodeId);
    if (!cur || ns.iteration > cur.iteration) latest.set(ns.nodeId, ns);
  }
  return latest;
}

/**
 * Order node ids the way the run actually reached them.
 *
 * The graph alone can't answer this: two independent branches are equally
 * valid in either order, so a plain topological sort has to break the tie on
 * something arbitrary (array position) and then disagrees with what the user
 * watched happen. `startedAt` — a timestamp, or any monotonic stand-in —
 * settles it for every node the run reached; the rest keep their graph
 * position at the back of whatever branch they belong to.
 *
 * The result is still topological, so a node never appears above one it
 * depends on.
 */
export function orderNodeIdsByExecution(
  nodes: readonly TopoNode[],
  edges: readonly TopoEdge[],
  startedAt: (nodeId: string) => number | null,
): string[] {
  const started = nodes
    .map((n) => ({ id: n.id, at: startedAt(n.id) }))
    .filter((entry): entry is { id: string; at: number } => entry.at !== null)
    .sort((a, b) => a.at - b.at);

  const rankById = new Map<string, number>();
  started.forEach((entry, i) => rankById.set(entry.id, i));

  return topoSortNodeIds(nodes, edges, (id, index) =>
    rankById.get(id) ?? started.length + index,
  );
}

/**
 * Execution log steps for a run read back from the API, ordered by
 * {@link orderNodeIdsByExecution} over the graph snapshot frozen at run start.
 */
export function persistedExecutionSteps(
  detail: ProcessRunDetailDto,
): ExecutionStep[] {
  const latest = latestStateByNode(detail.nodeStates);
  const { nodes: snapshot, edges } = detail.graphSnapshot;
  // Every computable node is a step, reached or not, so a run that stopped
  // early reads the same afterwards as it did live. A state the snapshot
  // doesn't know about is a step too, and goes at the back.
  const nodes = snapshot.filter((n) => n.kind !== 'note');
  const inSnapshot = new Set(nodes.map((n) => n.nodeId));

  const topoNodes: TopoNode[] = [
    ...nodes.map((n) => ({ id: n.nodeId })),
    ...[...latest.keys()].filter((id) => !inSnapshot.has(id)).map((id) => ({ id })),
  ];
  const topoEdges: TopoEdge[] = edges.map((e) => ({
    source: e.sourceNodeId,
    target: e.targetNodeId,
  }));

  const startedAt = (nodeId: string): number | null => {
    const at = latest.get(nodeId)?.startedAt;
    return at ? new Date(at).getTime() : null;
  };

  const labelByNodeId = new Map(nodes.map((n) => [n.nodeId, n.label]));

  return orderNodeIdsByExecution(topoNodes, topoEdges, startedAt).map((nodeId) => ({
    id: nodeId,
    title: labelByNodeId.get(nodeId) ?? nodeId,
    nodeStatus: latest.get(nodeId)?.status,
  }));
}
